import * as React from 'react';
import { Button } from '@helpcenter/analytics-components';
import type { dismissFlag as dismissFlagType } from '@helpcenter/flags';
import { ThemedButton } from '@helpcenter/themed-button';
import type { MessageDescriptor, WrappedComponentProps } from 'react-intl';
import type { showFeedbackCollector as showFeedbackCollectorType } from 'state/actions/feedback-collector';
import type { FeedbackCollectorPayload } from 'state/actions/feedback-collector/feedback-collector';
import type { Flag } from 'state/ui/flags';
import type { Appearance } from '@atlaskit/button/old-button/types';
import type { ButtonProps } from '@atlaskit/button/button';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { ButtonItem } from '@atlaskit/side-navigation/button-item';
import type { EnvType } from '@atlassian/help-center-common-util/env';

export interface FeedbackCollectorButtonProps {
	env: EnvType;
	flags: Flag[];
	showIcon: boolean;
	featureName: string;
	showTypeField: boolean;
	buttonLabel: MessageDescriptor;
	analyticsSubjectId: string;
	dismissFlagMessage?: MessageDescriptor;
	appearance?: Appearance;
	dismissFlag: typeof dismissFlagType;
	showFeedbackCollector: typeof showFeedbackCollectorType;
	spacing?: ButtonProps['spacing'];
	useTheme?: boolean;
	useSideNavButton?: boolean;
}

export const FeedbackCollectorButton: React.FC<
	FeedbackCollectorButtonProps & WrappedComponentProps
> = ({
	intl,
	env,
	flags,
	showIcon,
	featureName,
	showTypeField,
	buttonLabel,
	analyticsSubjectId,
	dismissFlagMessage,
	dismissFlag,
	showFeedbackCollector,
	appearance = 'subtle',
	spacing = 'default',
	useTheme = false,
	useSideNavButton = false,
}) => {
	const openFeedbackCollector = React.useCallback(() => {
		const showFeedbackCollectorPayload: FeedbackCollectorPayload = {
			env,
			showTypeField,
			featureName,
			shouldOpenFeedbackWidget: true,
		};

		if (dismissFlagMessage) {
			const successFlag = flags.filter(
				// @ts-ignore TS(2532) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Object is possibly 'undefined'.
				(flag) =>
					(flag.titleMessage.defaultMessage as string).localeCompare(
						dismissFlagMessage.defaultMessage as string,
					) === 0,
			)[0];

			successFlag && dismissFlag(successFlag.id);
		}

		showFeedbackCollector(showFeedbackCollectorPayload);
	}, [
		env,
		showTypeField,
		featureName,
		dismissFlagMessage,
		flags,
		dismissFlag,
		showFeedbackCollector,
	]);

	if (useSideNavButton) {
		return (
			<ButtonItem
				iconBefore={showIcon ? <FeedbackIcon spacing="spacious" label="" /> : undefined}
				onClick={openFeedbackCollector}
			>
				{intl.formatMessage(buttonLabel)}
			</ButtonItem>
		);
	}
	const ButtonComponent = useTheme ? ThemedButton : Button;
	return (
		<ButtonComponent
			iconBefore={showIcon ? <FeedbackIcon spacing="spacious" label="feedback" /> : undefined}
			appearance={appearance}
			onClick={openFeedbackCollector}
			actionSubjectId={analyticsSubjectId}
			spacing={spacing}
		>
			{intl.formatMessage(buttonLabel)}
		</ButtonComponent>
	);
};
